import type {
    CollaboratorAllocationSummaryEntry,
    CollaboratorPrivate,
    Resource,
} from '@/features/Collaborators/Models';
import {
    CLIENT_ALLOCATION_TYPES,
    EMPLOYMENT_TYPE_LABELS,
    INACTIVE_COLLABORATOR_STATUSES,
    INTERNAL_ALLOCATION_TYPES,
} from '../exportsPage.definitions';
import type {
    AllocationResponsibleMap,
    CapabilityNameMaps,
    CollaboratorListRow,
    CollaboratorSituation,
} from '../exportsPage.types';
import { parseDateOnly } from './parseDateOnly';

/** `status` do cadastro que marca o colaborador como desativado. */
function isInactive(status: string | null | undefined): boolean {
    const normalized = status?.trim().toLowerCase();
    return Boolean(normalized) && INACTIVE_COLLABORATOR_STATUSES.includes(normalized as never);
}

function hasAllocationOfType(
    active: CollaboratorAllocationSummaryEntry[],
    types: readonly string[]
): boolean {
    return active.some((entry) => entry.allocationType && types.includes(entry.allocationType));
}

/**
 * Deriva a situação do colaborador, na ordem: Desativado, Interno, Alocado,
 * Desalocado — a primeira que casar vence.
 *
 * Interno vem antes de Alocado: quem tem alocação interna vigente é
 * classificado como Interno mesmo que também tenha alocação em cliente.
 */
export function resolveCollaboratorSituation(
    collaborator: Pick<CollaboratorPrivate, 'status' | 'allocationSummary'>
): CollaboratorSituation {
    if (isInactive(collaborator.status)) {
        return 'Desativado';
    }

    const active = collaborator.allocationSummary?.active ?? [];

    if (hasAllocationOfType(active, INTERNAL_ALLOCATION_TYPES)) {
        return 'Interno';
    }

    if (hasAllocationOfType(active, CLIENT_ALLOCATION_TYPES)) {
        return 'Alocado';
    }

    return 'Desalocado';
}

/** Recurso marcado como principal, ou o primeiro cadastrado. */
function findPrimaryResource(resources: Resource[] | undefined): Resource | null {
    if (!resources || resources.length === 0) {
        return null;
    }
    return resources.find((resource) => resource.isPrimary) ?? resources[0];
}

/**
 * Habilidade principal no formato `Tecnologia • Tipo` (ex.: `Java • Backend`),
 * resolvendo os ids contra o catálogo de capacidades.
 */
function resolvePrimarySkill(
    resources: Resource[] | undefined,
    catalog: CapabilityNameMaps
): string {
    const primary = findPrimaryResource(resources);
    if (!primary) {
        return '';
    }

    const technology = catalog.technologyMap.get(primary.technologyId) ?? '';
    const type = catalog.resourceTypeMap.get(primary.typeId) ?? '';

    return [technology, type].filter(Boolean).join(' • ');
}

/** Senioridade do cadastro, caindo para a do recurso principal quando ausente. */
function resolveSeniority(
    collaborator: CollaboratorPrivate,
    catalog: CapabilityNameMaps
): string {
    const fromProfile = collaborator.seniorityName?.trim();
    if (fromProfile) {
        return fromProfile;
    }

    if (collaborator.seniorityId) {
        const resolved = catalog.seniorityMap.get(collaborator.seniorityId);
        if (resolved) {
            return resolved;
        }
    }

    const primary = findPrimaryResource(collaborator.resources);
    return primary ? catalog.seniorityMap.get(primary.seniorityId) ?? '' : '';
}

/** Clientes das alocações vigentes, sem repetição. */
function resolveAllocatedClients(collaborator: CollaboratorPrivate): string {
    const active = collaborator.allocationSummary?.active ?? [];
    const clients = active
        .filter((entry) => entry.allocationType && CLIENT_ALLOCATION_TYPES.includes(entry.allocationType as never))
        .map((entry) => entry.clientName?.trim())
        .filter((name): name is string => Boolean(name));

    return Array.from(new Set(clients)).sort((a, b) => a.localeCompare(b, 'pt-BR')).join(', ');
}

/**
 * Líder técnico: Responsável Raro da alocação vigente, caindo para o
 * "Responsável Raro" do cadastro do colaborador.
 *
 * Alocações em cliente têm prioridade sobre as demais (interna, comercial,
 * ausência); dentro de cada grupo vale a primeira alocação com responsável
 * preenchido. Quem não está alocado — ou cuja alocação não tem responsável —
 * fica com o responsável do cadastro.
 */
export function resolveTechnicalLeader(
    collaborator: CollaboratorPrivate,
    responsibles?: AllocationResponsibleMap
): string {
    const fromProfile = collaborator.responsibleName?.trim() ?? '';
    const byAllocation = responsibles?.get(collaborator.id);

    if (!byAllocation || byAllocation.size === 0) {
        return fromProfile;
    }

    const active = collaborator.allocationSummary?.active ?? [];
    const isClientAllocation = (entry: CollaboratorAllocationSummaryEntry) =>
        Boolean(entry.allocationType && CLIENT_ALLOCATION_TYPES.includes(entry.allocationType as never));

    const ordered = [...active.filter(isClientAllocation), ...active.filter((entry) => !isClientAllocation(entry))];

    for (const entry of ordered) {
        const responsible = byAllocation.get(entry.id)?.trim();
        if (responsible) {
            return responsible;
        }
    }

    return fromProfile;
}

/**
 * Monta a listagem completa de colaboradores — uma linha por colaborador,
 * ordenada por nome.
 *
 * `terminationReason` sai sempre vazia: não existe campo de motivo de
 * desligamento no cadastro, e a coluna fica reservada para preenchimento
 * manual até que exista.
 */
export function buildCollaboratorListRows(
    collaborators: CollaboratorPrivate[],
    catalog: CapabilityNameMaps,
    allocationResponsibles?: AllocationResponsibleMap
): CollaboratorListRow[] {
    return collaborators
        .map((collaborator) => ({
            id: collaborator.id,
            name: collaborator.name?.trim() ?? '',
            situation: resolveCollaboratorSituation(collaborator),
            seniorityName: resolveSeniority(collaborator, catalog),
            primarySkill: resolvePrimarySkill(collaborator.resources, catalog),
            employmentType: collaborator.collaboratorType
                ? EMPLOYMENT_TYPE_LABELS[collaborator.collaboratorType] ?? collaborator.collaboratorType
                : '',
            admissionDate: parseDateOnly(collaborator.startDate),
            terminationDate: parseDateOnly(collaborator.endDate),
            terminationReason: '',
            technicalLeader: resolveTechnicalLeader(collaborator, allocationResponsibles),
            allocatedClient: resolveAllocatedClients(collaborator),
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}
